import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import apiClient from "@/lib/apiClient";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import {
  Loader2, Ship, LogOut, Building2, Users, FolderKanban,
  MessageSquare, Globe, Gauge, ArrowLeft, TrendingUp, Inbox, CreditCard, RotateCcw,
  LifeBuoy, Send, CheckCircle2, Trash2, Activity, Eye, ExternalLink, Radio, Sparkles,
} from "lucide-react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";

const PLAN_META = {
  solo: { label: "Solo", color: "bg-white text-ink" },
  team: { label: "Team", color: "bg-teal text-ink" },
  business: { label: "Business", color: "bg-orange text-white" },
};

const TABS = [
  { id: "overview", label: "Overview", icon: Gauge },
  { id: "orgs", label: "Organizations", icon: Building2 },
  { id: "users", label: "Users", icon: Users },
  { id: "traffic", label: "Traffic", icon: Activity },
  { id: "geo", label: "Geo", icon: Globe },
  { id: "leads", label: "Leads", icon: Inbox },
  { id: "support", label: "Support", icon: LifeBuoy },
  { id: "billing", label: "Billing", icon: CreditCard },
  { id: "growth", label: "Growth", icon: Sparkles },
];

function fmtDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" }); }
  catch { return "—"; }
}

function fmtRelative(iso) {
  if (!iso) return "Never";
  try {
    const then = new Date(iso).getTime();
    const diff = Date.now() - then;
    if (diff < 0) return "just now";
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return "just now";
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    const days = Math.floor(hrs / 24);
    if (days < 30) return `${days}d ago`;
    return fmtDate(iso);
  } catch { return "Never"; }
}

export default function PlatformAdmin() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("overview");
  const [stats, setStats] = useState(null);
  const [orgs, setOrgs] = useState(null);
  const [users, setUsers] = useState(null);
  const [geo, setGeo] = useState(null);
  const [trend, setTrend] = useState(null);
  const [leads, setLeads] = useState(null);
  const [tickets, setTickets] = useState(null);
  const [traffic, setTraffic] = useState(null);

  const loadAll = () => {
    apiClient.get("/platform/stats").then(({ data }) => setStats(data)).catch(() => setStats({}));
    apiClient.get("/platform/organizations").then(({ data }) => setOrgs(data)).catch(() => setOrgs([]));
    apiClient.get("/platform/users").then(({ data }) => setUsers(data)).catch(() => setUsers([]));
    apiClient.get("/platform/geo").then(({ data }) => setGeo(data)).catch(() => setGeo({ countries: [], untracked: 0 }));
    apiClient.get("/platform/trend", { params: { days: 30 } }).then(({ data }) => setTrend(data)).catch(() => setTrend({ series: [], total: 0 }));
    apiClient.get("/platform/traffic", { params: { days: 30 } }).then(({ data }) => setTraffic(data)).catch(() => setTraffic(null));
    apiClient.get("/platform/leads").then(({ data }) => setLeads(data)).catch(() => setLeads([]));
    apiClient.get("/platform/support").then(({ data }) => setTickets(data)).catch(() => setTickets([]));
  };

  useEffect(() => { loadAll(); }, []);

  const changePlan = async (orgId, plan) => {
    try {
      await apiClient.post(`/platform/org/${orgId}/plan`, { plan });
      setOrgs((prev) => prev.map((o) => (o.id === orgId ? { ...o, plan } : o)));
      toast.success(`Plan updated to ${PLAN_META[plan].label}`);
      apiClient.get("/platform/stats").then(({ data }) => setStats(data)).catch(() => {});
    } catch (e) {
      toast.error("Failed to update plan");
    }
  };

  const handleLogout = async () => { await logout(); navigate("/login"); };

  return (
    <div className="min-h-screen bg-ink text-cream" data-testid="platform-admin">
      {/* Top command bar */}
      <header className="sticky top-0 z-30 bg-ink border-b-2 border-orange/60">
        <div className="max-w-[1300px] mx-auto px-4 sm:px-6 h-16 flex items-center justify-between gap-2">
          <div className="flex items-center gap-3 min-w-0">
            <div className="h-9 w-9 bg-orange border-2 border-cream rounded-xl flex items-center justify-center shrink-0">
              <Ship className="h-5 w-5 text-white" />
            </div>
            <div className="leading-tight min-w-0">
              <p className="font-display font-700 text-base sm:text-lg tracking-tight truncate"><span className="hidden sm:inline">Tugboat · </span>Mission Control</p>
              <p className="text-[11px] text-cream/50 font-mono uppercase tracking-widest truncate">Super Admin · {user?.email}</p>
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <button onClick={() => navigate("/dashboard")} data-testid="back-to-dashboard"
              className="flex items-center gap-2 px-3 py-2 rounded-xl text-sm font-bold border-2 border-cream/30 hover:border-cream hover:bg-cream/10 transition-colors">
              <ArrowLeft className="h-4 w-4" /> <span className="hidden sm:inline">Dashboard</span>
            </button>
            <button onClick={handleLogout} data-testid="platform-logout"
              className="flex items-center gap-2 px-3 py-2 rounded-xl text-sm font-bold border-2 border-cream/30 hover:border-danger hover:bg-danger hover:text-white transition-colors">
              <LogOut className="h-4 w-4" /> <span className="hidden sm:inline">Sign out</span>
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-[1300px] mx-auto px-6 py-8">
        {/* Tabs */}
        <div className="flex flex-wrap gap-2 mb-8">
          {TABS.map((t) => (
            <button key={t.id} onClick={() => setTab(t.id)} data-testid={`platform-tab-${t.id}`}
              className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-bold border-2 transition-all ${
                tab === t.id ? "bg-orange border-orange text-white" : "border-cream/25 text-cream/70 hover:border-cream/60 hover:text-cream"
              }`}>
              <t.icon className="h-4 w-4" /> {t.label}
            </button>
          ))}
        </div>

        {tab === "overview" && <Overview stats={stats} trend={trend} />}
        {tab === "orgs" && <Organizations orgs={orgs} changePlan={changePlan} reload={() => apiClient.get("/platform/organizations").then(({ data }) => setOrgs(data)).catch(() => {})} />}
        {tab === "users" && <UsersTable users={users} reload={() => apiClient.get("/platform/users").then(({ data }) => setUsers(data)).catch(() => {})} />}
        {tab === "traffic" && <Traffic traffic={traffic} />}
        {tab === "geo" && <Geo geo={geo} />}
        {tab === "leads" && <Leads leads={leads} />}
        {tab === "support" && <Support tickets={tickets} reload={() => apiClient.get("/platform/support").then(({ data }) => setTickets(data)).catch(() => {})} />}
        {tab === "billing" && <BillingAdmin />}
        {tab === "growth" && <PromoAdmin />}
      </div>
    </div>
  );
}

function Loading() {
  return <div className="flex items-center justify-center py-24"><Loader2 className="h-7 w-7 animate-spin text-orange" /></div>;
}

function MetricCard({ label, value, icon: Icon, accent }) {
  return (
    <div className="rounded-2xl border-2 border-cream/20 bg-cream/[0.04] p-6 relative overflow-hidden" data-testid={`metric-${label.toLowerCase().replace(/\s/g, "-")}`}>
      <Icon className={`absolute -right-3 -bottom-3 h-20 w-20 opacity-10 ${accent}`} />
      <p className="text-[11px] font-800 uppercase tracking-widest text-cream/50 mb-3">{label}</p>
      <p className="font-display text-5xl font-700 leading-none">{value ?? "—"}</p>
    </div>
  );
}

function Overview({ stats, trend }) {
  if (!stats) return <Loading />;
  const pd = stats.plan_distribution || {};
  const bs = stats.by_status || {};
  return (
    <div className="space-y-8">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-5">
        <MetricCard label="Organizations" value={stats.total_orgs} icon={Building2} accent="text-teal" />
        <MetricCard label="Users" value={stats.total_users} icon={Users} accent="text-sunny" />
        <MetricCard label="Projects" value={stats.total_projects} icon={FolderKanban} accent="text-orange" />
        <MetricCard label="Feedback" value={stats.total_feedback} icon={MessageSquare} accent="text-grass" />
      </div>

      <TrendChart trend={trend} />

      <div className="grid lg:grid-cols-2 gap-5">
        <div className="rounded-2xl border-2 border-cream/20 bg-cream/[0.04] p-6">
          <p className="text-[11px] font-800 uppercase tracking-widest text-cream/50 mb-4">Plan distribution</p>
          <div className="space-y-3">
            {["solo", "team", "business"].map((p) => (
              <div key={p} className="flex items-center justify-between" data-testid={`plan-dist-${p}`}>
                <span className="flex items-center gap-2 font-700 text-sm">
                  <span className={`px-2.5 py-1 rounded-lg text-xs border-2 border-ink ${PLAN_META[p].color}`}>{PLAN_META[p].label}</span>
                </span>
                <span className="font-mono text-xl font-700">{pd[p] ?? 0}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="rounded-2xl border-2 border-cream/20 bg-cream/[0.04] p-6">
          <p className="text-[11px] font-800 uppercase tracking-widest text-cream/50 mb-4">Feedback by status</p>
          <div className="space-y-3">
            {[["new", "New", "bg-danger"], ["in_progress", "In progress", "bg-sunny text-ink"], ["resolved", "Resolved", "bg-grass"]].map(([k, label, c]) => (
              <div key={k} className="flex items-center justify-between" data-testid={`status-${k}`}>
                <span className={`px-2.5 py-1 rounded-lg text-xs font-700 border-2 border-ink ${c}`}>{label}</span>
                <span className="font-mono text-xl font-700">{bs[k] ?? 0}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function Organizations({ orgs, changePlan, reload }) {
  const [busy, setBusy] = useState("");
  const [detailId, setDetailId] = useState(null);
  if (!orgs) return <Loading />;
  if (orgs.length === 0) return <Empty label="No organizations yet." />;

  const deleteOrg = async (o) => {
    if (!window.confirm(`Delete "${o.name}"? This permanently removes the org plus its ${o.members} member(s), ${o.projects} project(s) and ${o.feedback} feedback item(s). This cannot be undone.`)) return;
    setBusy(o.id);
    try {
      await apiClient.delete(`/platform/organizations/${o.id}`);
      toast.success(`Deleted "${o.name}"`);
      reload();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to delete organization");
    } finally {
      setBusy("");
    }
  };

  return (
    <div className="rounded-2xl border-2 border-cream/20 overflow-hidden" data-testid="orgs-table">
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-cream/[0.06] text-cream/60 text-[11px] uppercase tracking-widest font-800">
              <Th>Organization</Th><Th>Owner</Th><Th className="text-center">Members</Th>
              <Th className="text-center">Projects</Th><Th className="text-center">Feedback</Th>
              <Th>Created</Th><Th>Last active</Th><Th>Plan</Th><Th>Feature overrides <span className="normal-case font-600 text-cream/40">(testing)</span></Th><Th></Th>
            </tr>
          </thead>
          <tbody className="divide-y divide-cream/10">
            {orgs.map((o) => (
              <tr key={o.id} className="hover:bg-cream/[0.03]" data-testid={`org-row-${o.id}`}>
                <Td className="font-700"><button onClick={() => setDetailId(o.id)} data-testid={`org-detail-open-${o.id}`} className="hover:text-orange transition-colors text-left">{o.name}</button></Td>
                <Td className="text-cream/70">{o.owner_email || "—"}</Td>
                <Td className="text-center font-mono">{o.members}</Td>
                <Td className="text-center font-mono">{o.projects}</Td>
                <Td className="text-center font-mono">{o.feedback}</Td>
                <Td className="text-cream/60 font-mono text-xs">{fmtDate(o.created_at)}</Td>
                <Td className={`font-mono text-xs whitespace-nowrap ${o.last_active ? "text-cream/70" : "text-cream/30"}`} data-testid={`org-last-active-${o.id}`} title={o.last_active ? fmtDate(o.last_active) : ""}>{fmtRelative(o.last_active)}</Td>
                <Td>
                  <select
                    value={o.plan}
                    onChange={(e) => changePlan(o.id, e.target.value)}
                    data-testid={`org-plan-select-${o.id}`}
                    className="bg-ink border-2 border-cream/30 rounded-lg px-2 py-1.5 text-sm font-700 text-cream focus:border-orange outline-none cursor-pointer"
                  >
                    <option value="solo">Solo</option>
                    <option value="team">Team</option>
                    <option value="business">Business</option>
                  </select>
                </Td>
                <Td><FeatureToggles org={o} /></Td>
                <Td>
                  <div className="flex items-center gap-2">
                    <button onClick={() => setDetailId(o.id)} data-testid={`view-org-${o.id}`} title={`View ${o.name}`}
                      className="text-cream/40 hover:text-teal transition-colors">
                      <Eye className="h-4 w-4" />
                    </button>
                    <button onClick={() => deleteOrg(o)} disabled={busy === o.id}
                      data-testid={`delete-org-${o.id}`} title={`Delete ${o.name}`}
                      className="text-cream/40 hover:text-danger transition-colors disabled:opacity-40">
                      {busy === o.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
                    </button>
                  </div>
                </Td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <OrgDetailDrawer orgId={detailId} onClose={() => setDetailId(null)} />
    </div>
  );
}

function OrgDetailDrawer({ orgId, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = () => {
    if (!orgId) { setData(null); return; }
    setLoading(true);
    apiClient.get(`/platform/organizations/${orgId}`)
      .then(({ data }) => setData(data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [orgId]);

  const trialAction = async (action, days) => {
    setBusy(true);
    try {
      await apiClient.post(`/platform/org/${orgId}/trial`, { action, days });
      toast.success(action === "end" ? "Trial ended" : (action === "extend" ? `Trial extended by ${days} days` : `Trial reset to ${days} days`));
      load();
    } catch (e) {
      toast.error("Could not update trial");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Sheet open={!!orgId} onOpenChange={(o) => { if (!o) onClose(); }}>
      <SheetContent side="right" className="w-full sm:max-w-lg bg-ink text-cream border-l-2 border-cream/20 overflow-y-auto" data-testid="org-detail-drawer">
        <SheetHeader>
          <SheetTitle className="text-cream font-display text-2xl">{data?.name || "Organization"}</SheetTitle>
        </SheetHeader>
        {loading || !data ? (
          <div className="h-40 flex items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-orange" /></div>
        ) : (
          <div className="mt-6 space-y-6">
            <div className="grid grid-cols-2 gap-3">
              <Stat label="Plan" value={data.plan} />
              <Stat label="Feedback" value={data.total_feedback} />
              <Stat label="Members" value={data.members.length} />
              <Stat label="Projects" value={data.projects.length} />
            </div>
            <div className="text-sm text-cream/60 font-600">
              Owner: <span className="text-cream font-700">{data.owner.name || "—"}</span> · {data.owner.email || "—"}
              <br />Created {fmtDate(data.created_at)}{data.last_activity ? ` · Last activity ${fmtDate(data.last_activity)}` : ""}
              <br /><span data-testid="org-detail-last-active">Last active {data.last_active ? fmtRelative(data.last_active) : "Never"}</span>
            </div>

            {/* Business trial control */}
            <div className="rounded-2xl border-2 border-cream/15 p-4" data-testid="org-trial-control">
              <p className="text-[11px] font-800 uppercase tracking-widest text-cream/50 mb-2 flex items-center gap-2"><Sparkles className="h-4 w-4" /> Business trial</p>
              <p className={`text-sm font-800 mb-3 ${data.on_trial ? "text-sunny" : "text-cream/50"}`} data-testid="org-trial-status">
                {data.on_trial
                  ? `Active · ${data.trial_days_left} day${data.trial_days_left === 1 ? "" : "s"} left`
                  : (data.trial_ends_at ? "Expired" : "No trial")}
              </p>
              <div className="flex flex-wrap gap-2">
                <button disabled={busy} onClick={() => trialAction("extend", 30)} data-testid="trial-extend-30"
                  className="px-3 py-1.5 rounded-lg border-2 border-cream/30 text-xs font-800 hover:bg-cream hover:text-ink transition-colors disabled:opacity-50">+30 days</button>
                <button disabled={busy} onClick={() => trialAction("set", 90)} data-testid="trial-set-90"
                  className="px-3 py-1.5 rounded-lg border-2 border-cream/30 text-xs font-800 hover:bg-cream hover:text-ink transition-colors disabled:opacity-50">Reset 90 days</button>
                {data.on_trial && (
                  <button disabled={busy} onClick={() => trialAction("end")} data-testid="trial-end"
                    className="px-3 py-1.5 rounded-lg border-2 border-danger text-danger text-xs font-800 hover:bg-danger hover:text-white transition-colors disabled:opacity-50">End now</button>
                )}
              </div>
            </div>

            {/* Installed on */}
            <div>
              <p className="text-[11px] font-800 uppercase tracking-widest text-cream/50 mb-3 flex items-center gap-2"><Globe className="h-4 w-4" /> Installed on ({data.domains.length})</p>
              {data.domains.length === 0 ? (
                <p className="text-cream/40 text-sm">No domains detected yet — the widget hasn't been loaded or received feedback anywhere.</p>
              ) : (
                <div className="space-y-2" data-testid="org-domains-list">
                  {data.domains.map((d) => (
                    <div key={d.domain} className="flex items-center justify-between gap-3 rounded-xl border-2 border-cream/15 px-3 py-2.5">
                      <div className="min-w-0">
                        <a href={`https://${d.domain}`} target="_blank" rel="noreferrer" className="font-700 text-sm hover:text-orange transition-colors flex items-center gap-1.5 truncate">
                          {d.domain} <ExternalLink className="h-3 w-3 shrink-0" />
                        </a>
                        <span className="text-[11px] text-cream/50 font-600">
                          {d.count > 0 ? `${d.count} feedback` : (
                            <span className="inline-flex items-center gap-1 text-sunny"><Radio className="h-3 w-3" /> installed, no feedback yet</span>
                          )}
                          {d.last_seen ? ` · seen ${fmtDate(d.last_seen)}` : ""}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Projects */}
            <div>
              <p className="text-[11px] font-800 uppercase tracking-widest text-cream/50 mb-3 flex items-center gap-2"><FolderKanban className="h-4 w-4" /> Projects ({data.projects.length})</p>
              <div className="space-y-2">
                {data.projects.map((p) => (
                  <div key={p.id} className="flex items-center justify-between gap-3 rounded-xl border-2 border-cream/15 px-3 py-2.5">
                    <span className="font-700 text-sm truncate">{p.name}</span>
                    <span className="text-xs font-mono text-cream/50">{p.feedback} fb</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}

function Stat({ label, value }) {
  return (
    <div className="rounded-2xl border-2 border-cream/20 bg-cream/[0.04] p-4">
      <p className="text-2xl font-900 font-display capitalize">{value}</p>
      <p className="text-[11px] text-cream/50 font-700 mt-0.5">{label}</p>
    </div>
  );
}

function FeatureToggles({ org }) {
  const ov = org.feature_overrides || {};
  const [replay, setReplay] = useState(!!ov.session_replay);
  const [branding, setBranding] = useState(!!ov.branding);
  const [rec, setRec] = useState(ov.recording_secs ? String(ov.recording_secs) : "");
  const [saving, setSaving] = useState(false);

  const save = async (next) => {
    setSaving(true);
    try {
      await apiClient.put(`/platform/org/${org.id}/features`, {
        session_replay: next.replay,
        branding: next.branding,
        recording_secs: next.rec === "" ? null : Number(next.rec),
      });
      toast.success(`Overrides updated for ${org.name}`);
    } catch {
      toast.error("Failed to update feature overrides");
    } finally {
      setSaving(false);
    }
  };

  const toggle = (key) => {
    const next = { replay, branding, rec };
    if (key === "replay") { next.replay = !replay; setReplay(next.replay); }
    if (key === "branding") { next.branding = !branding; setBranding(next.branding); }
    save(next);
  };

  const chip = (active, label, key) => (
    <button
      type="button"
      onClick={() => toggle(key)}
      disabled={saving}
      data-testid={`feature-${key}-${org.id}`}
      aria-pressed={active}
      className={`px-2.5 py-1 rounded-full text-[11px] font-800 border-2 transition-colors disabled:opacity-50 ${
        active ? "bg-grass border-grass text-white" : "bg-transparent border-cream/30 text-cream/60 hover:border-cream/60"
      }`}
    >
      {active ? "✓ " : ""}{label}
    </button>
  );

  return (
    <div className="flex items-center gap-1.5" data-testid={`feature-overrides-${org.id}`}>
      {chip(replay, "Replay", "replay")}
      {chip(branding, "Branding", "branding")}
      <input
        type="number"
        min="5"
        max="3600"
        value={rec}
        placeholder="rec s"
        onChange={(e) => setRec(e.target.value)}
        onBlur={() => save({ replay, branding, rec })}
        data-testid={`feature-rec-${org.id}`}
        title="Override max recording length (seconds). Blank = plan default."
        className="w-[68px] bg-ink border-2 border-cream/30 rounded-lg px-2 py-1 text-xs font-700 text-cream focus:border-orange outline-none"
      />
    </div>
  );
}

function UsersTable({ users, reload }) {
  const [selected, setSelected] = useState([]);
  const [busy, setBusy] = useState(false);

  if (!users) return <Loading />;
  if (users.length === 0) return <Empty label="No users yet." />;

  const deletable = users.filter((u) => !u.is_staff);
  const toggle = (id) => setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  const allSelected = deletable.length > 0 && selected.length === deletable.length;
  const toggleAll = () => setSelected(allSelected ? [] : deletable.map((u) => u.user_id));

  const runDelete = async (ids, label) => {
    if (!ids.length) return;
    if (!window.confirm(`Delete ${label}? This permanently removes the ${ids.length === 1 ? "account" : ids.length + " accounts"} and any now-empty organizations. This cannot be undone.`)) return;
    setBusy(true);
    try {
      let res;
      if (ids.length === 1) { ({ data: res } = await apiClient.delete(`/platform/users/${ids[0]}`)); }
      else { ({ data: res } = await apiClient.post("/platform/users/bulk-delete", { user_ids: ids })); }
      const n = res.deleted_count ?? (res.deleted || []).length;
      toast.success(`Deleted ${n} user${n === 1 ? "" : "s"}${res.skipped?.length ? ` · ${res.skipped.length} skipped` : ""}`);
      setSelected([]);
      reload();
    } catch {
      toast.error("Failed to delete users");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="rounded-2xl border-2 border-cream/20 overflow-hidden" data-testid="users-table">
      <div className="flex items-center justify-between gap-3 px-4 py-3 bg-cream/[0.06] border-b-2 border-cream/15">
        <p className="text-xs font-800 text-cream/60">{users.length} users · {selected.length} selected</p>
        <button
          onClick={() => runDelete(selected, `${selected.length} selected user${selected.length === 1 ? "" : "s"}`)}
          disabled={busy || selected.length === 0}
          data-testid="delete-selected-users-btn"
          className="flex items-center gap-2 text-xs font-800 px-3 py-2 rounded-xl border-2 border-danger text-danger hover:bg-danger hover:text-white transition-colors disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent disabled:hover:text-danger">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />} Delete selected
        </button>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-cream/[0.06] text-cream/60 text-[11px] uppercase tracking-widest font-800">
              <th className="px-4 py-3 w-10">
                <input type="checkbox" checked={allSelected} onChange={toggleAll} data-testid="select-all-users"
                  className="h-4 w-4 accent-orange cursor-pointer" aria-label="Select all deletable users" />
              </th>
              <Th>Name</Th><Th>Email</Th><Th>Org</Th><Th>Role</Th><Th>Provider</Th><Th>Joined</Th><Th></Th>
            </tr>
          </thead>
          <tbody className="divide-y divide-cream/10">
            {users.map((u) => (
              <tr key={u.user_id} className={`hover:bg-cream/[0.03] ${selected.includes(u.user_id) ? "bg-orange/10" : ""}`} data-testid={`user-row-${u.user_id}`}>
                <Td>
                  {u.is_staff
                    ? <span className="block h-4 w-4" title="Staff accounts can't be deleted" />
                    : <input type="checkbox" checked={selected.includes(u.user_id)} onChange={() => toggle(u.user_id)}
                        data-testid={`select-user-${u.user_id}`} className="h-4 w-4 accent-orange cursor-pointer" />}
                </Td>
                <Td className="font-700">{u.name || "—"}</Td>
                <Td className="text-cream/70">{u.email}</Td>
                <Td className="text-cream/60">{u.org_name || "—"}</Td>
                <Td>
                  <span className={`px-2 py-0.5 rounded-md text-xs font-700 border ${u.is_staff ? "bg-orange text-white border-orange" : "border-cream/30 text-cream/70"}`}>
                    {u.is_staff ? "staff" : u.role}
                  </span>
                </Td>
                <Td className="text-cream/60 capitalize">{u.auth_provider}</Td>
                <Td className="text-cream/60 font-mono text-xs">{fmtDate(u.created_at)}</Td>
                <Td>
                  {!u.is_staff && (
                    <button onClick={() => runDelete([u.user_id], u.email)} disabled={busy}
                      data-testid={`delete-user-${u.user_id}`} title={`Delete ${u.email}`}
                      className="text-cream/40 hover:text-danger transition-colors disabled:opacity-40">
                      <Trash2 className="h-4 w-4" />
                    </button>
                  )}
                </Td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function BarList({ title, rows, color = "bg-teal", testid, empty }) {
  const data = rows || [];
  const max = data.reduce((m, r) => Math.max(m, r.count), 0) || 1;
  return (
    <div className="rounded-2xl border-2 border-cream/20 bg-cream/[0.04] p-6" data-testid={testid}>
      <p className="text-[11px] font-800 uppercase tracking-widest text-cream/50 mb-5">{title}</p>
      {data.length === 0 ? (
        <p className="text-cream/40 text-sm py-6 text-center">{empty || "No data yet."}</p>
      ) : (
        <div className="space-y-3">
          {data.map((r) => (
            <div key={r.key} className="flex items-center gap-4">
              <span className="w-36 truncate font-700 text-sm" title={r.key}>{r.key}</span>
              <div className="flex-1 h-3 rounded-full bg-cream/10 overflow-hidden">
                <div className={`h-full ${color} rounded-full`} style={{ width: `${(r.count / max) * 100}%` }} />
              </div>
              <span className="w-10 text-right font-mono text-sm">{r.count}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function Traffic({ traffic }) {
  if (!traffic) return <Loading />;
  const series = traffic.series || [];
  const cards = [
    { label: "Visits", value: traffic.total_visits, icon: Activity, color: "text-teal" },
    { label: "Unique visitors", value: traffic.unique_visitors, icon: Users, color: "text-sunny" },
    { label: "Signups", value: traffic.signups ?? 0, icon: TrendingUp, color: "text-grass" },
    { label: "Conversion", value: `${traffic.conversion_rate}%`, icon: Gauge, color: "text-orange" },
  ];
  return (
    <div className="space-y-5" data-testid="traffic-panel">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {cards.map((c) => (
          <div key={c.label} className="rounded-2xl border-2 border-cream/20 bg-cream/[0.04] p-5" data-testid={`traffic-card-${c.label.toLowerCase().replace(/\s/g, "-")}`}>
            <c.icon className={`h-5 w-5 mb-2 ${c.color}`} />
            <p className="text-3xl font-900 font-display">{c.value ?? 0}</p>
            <p className="text-xs text-cream/50 font-700 mt-1">{c.label}</p>
          </div>
        ))}
      </div>

      <div className="rounded-2xl border-2 border-cream/20 bg-cream/[0.04] p-6">
        <p className="text-[11px] font-800 uppercase tracking-widest text-cream/50 mb-4">Visits · last {traffic.days} days</p>
        {series.length === 0 ? (
          <p className="text-cream/40 text-sm py-8 text-center">No visits tracked yet. The tracker fires on your public marketing pages.</p>
        ) : (
          <ResponsiveContainer width="100%" height={200}>
            <AreaChart data={series} margin={{ top: 4, right: 8, left: -20, bottom: 0 }}>
              <defs>
                <linearGradient id="tfill" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#4ECDC4" stopOpacity={0.5} />
                  <stop offset="100%" stopColor="#4ECDC4" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,250,240,0.08)" />
              <XAxis dataKey="date" tick={{ fill: "rgba(255,250,240,0.4)", fontSize: 10 }} tickFormatter={(d) => d.slice(5)} interval="preserveStartEnd" />
              <YAxis tick={{ fill: "rgba(255,250,240,0.4)", fontSize: 10 }} allowDecimals={false} />
              <Tooltip contentStyle={{ background: "#1A1A1A", border: "2px solid #4ECDC4", borderRadius: 12, color: "#fff" }} />
              <Area type="monotone" dataKey="count" stroke="#4ECDC4" strokeWidth={2.5} fill="url(#tfill)" />
            </AreaChart>
          </ResponsiveContainer>
        )}
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <BarList title="Channels — how they arrived" rows={traffic.by_channel} color="bg-orange" testid="traffic-channels" empty="No channel data yet." />
        <BarList title="Signups by channel" rows={traffic.conversions_by_channel} color="bg-grass" testid="traffic-conversions" empty="No signups attributed yet." />
        <BarList title="Top referrers" rows={traffic.top_referrers} color="bg-teal" testid="traffic-referrers" empty="No referrers yet — visitors came directly." />
        <BarList title="Top campaign sources (UTM)" rows={traffic.top_sources} color="bg-sunny" testid="traffic-sources" empty="No UTM-tagged visits yet." />
        <BarList title="Top landing pages" rows={traffic.top_landing} color="bg-teal" testid="traffic-landing" empty="No visits yet." />
        <BarList title="Visitors by country" rows={traffic.by_country} color="bg-orange" testid="traffic-countries" empty="No geo data yet." />
      </div>
    </div>
  );
}

function Geo({ geo }) {
  if (!geo) return <Loading />;
  const countries = geo.countries || [];
  const max = countries.reduce((m, c) => Math.max(m, c.count), 0) || 1;
  return (
    <div className="space-y-5">
      <div className="rounded-2xl border-2 border-cream/20 bg-cream/[0.04] p-6" data-testid="geo-table">
        <div className="flex items-center justify-between mb-5">
          <p className="text-[11px] font-800 uppercase tracking-widest text-cream/50">Feedback by country</p>
          <span className="text-xs text-cream/50 font-mono">{geo.untracked} untracked</span>
        </div>
        {countries.length === 0 ? (
          <p className="text-cream/50 text-sm py-8 text-center">No geo-tagged feedback yet. Geo is captured from tester IPs on widget submissions.</p>
        ) : (
          <div className="space-y-3">
            {countries.map((c) => (
              <div key={c.country} className="flex items-center gap-4" data-testid={`geo-row-${c.country}`}>
                <span className="w-40 truncate font-700 text-sm">{c.country}</span>
                <div className="flex-1 h-3 rounded-full bg-cream/10 overflow-hidden">
                  <div className="h-full bg-teal rounded-full" style={{ width: `${(c.count / max) * 100}%` }} />
                </div>
                <span className="w-10 text-right font-mono text-sm">{c.count}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function TrendChart({ trend }) {
  if (!trend) return (
    <div className="rounded-2xl border-2 border-cream/20 bg-cream/[0.04] p-6 flex items-center justify-center h-56">
      <Loader2 className="h-6 w-6 animate-spin text-orange" />
    </div>
  );
  const series = trend.series || [];
  const fmtTick = (d) => { try { const x = new Date(d + "T00:00:00"); return `${x.getMonth() + 1}/${x.getDate()}`; } catch { return d; } };
  return (
    <div className="rounded-2xl border-2 border-cream/20 bg-cream/[0.04] p-6" data-testid="trend-chart">
      <div className="flex items-center justify-between mb-5">
        <p className="text-[11px] font-800 uppercase tracking-widest text-cream/50 flex items-center gap-2">
          <TrendingUp className="h-4 w-4 text-teal" /> Feedback trend · last {trend.days || 30} days
        </p>
        <span className="font-mono text-sm text-cream/70">{trend.total} total</span>
      </div>
      <div style={{ width: "100%", height: 220 }}>
        <ResponsiveContainer>
          <AreaChart data={series} margin={{ top: 6, right: 8, left: -20, bottom: 0 }}>
            <defs>
              <linearGradient id="trendFill" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#4ECDC4" stopOpacity={0.55} />
                <stop offset="100%" stopColor="#4ECDC4" stopOpacity={0.02} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,250,240,0.08)" vertical={false} />
            <XAxis dataKey="date" tickFormatter={fmtTick} tick={{ fill: "rgba(255,250,240,0.5)", fontSize: 11 }} axisLine={false} tickLine={false} minTickGap={24} />
            <YAxis allowDecimals={false} tick={{ fill: "rgba(255,250,240,0.5)", fontSize: 11 }} axisLine={false} tickLine={false} width={36} />
            <Tooltip
              contentStyle={{ background: "#1A1A1A", border: "2px solid #FF6B35", borderRadius: 12, color: "#FFFAF0", fontSize: 12 }}
              labelStyle={{ color: "#FFFAF0", fontWeight: 700 }}
              labelFormatter={(d) => fmtTick(d)}
              formatter={(v) => [v, "feedback"]}
            />
            <Area type="monotone" dataKey="count" stroke="#4ECDC4" strokeWidth={2.5} fill="url(#trendFill)" dot={false} activeDot={{ r: 4, fill: "#FF6B35" }} />
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

function BillingAdmin() {
  const [cfg, setCfg] = useState(null);
  const [data, setData] = useState(null);
  const [form, setForm] = useState({ stripe_secret_key: "", team_price_id: "", business_price_id: "" });
  const [saving, setSaving] = useState(false);

  const load = () => {
    apiClient.get("/platform/billing/config").then(({ data }) => {
      setCfg(data);
      setForm((f) => ({ ...f, team_price_id: data.team_price_id || "", business_price_id: data.business_price_id || "" }));
    }).catch(() => setCfg({}));
    apiClient.get("/platform/billing/transactions").then(({ data }) => setData(data)).catch(() => setData({ transactions: [], total_collected: 0, paid_count: 0 }));
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    setSaving(true);
    try {
      const payload = { team_price_id: form.team_price_id, business_price_id: form.business_price_id };
      if (form.stripe_secret_key) payload.stripe_secret_key = form.stripe_secret_key;
      await apiClient.post("/platform/billing/config", payload);
      toast.success("Billing settings saved");
      setForm((f) => ({ ...f, stripe_secret_key: "" }));
      load();
    } catch { toast.error("Failed to save"); } finally { setSaving(false); }
  };

  const refund = async (sid) => {
    try { const { data } = await apiClient.post(`/platform/billing/refund/${sid}`); toast.success("Refund issued"); load(); }
    catch (err) { toast.error(err.response?.data?.detail || "Refund failed"); }
  };

  if (!cfg || !data) return <Loading />;
  return (
    <div className="space-y-6" data-testid="billing-admin">
      <div className="grid lg:grid-cols-3 gap-5">
        <MetricCard label="Collected" value={`$${data.total_collected}`} icon={CreditCard} accent="text-grass" />
        <MetricCard label="Paid" value={data.paid_count} icon={TrendingUp} accent="text-teal" />
        <MetricCard label="Refunded" value={data.refunded_count || 0} icon={RotateCcw} accent="text-sunny" />
      </div>

      <div className="rounded-2xl border-2 border-cream/20 bg-cream/[0.04] p-6" data-testid="billing-config">
        <div className="flex items-center justify-between mb-1">
          <p className="text-[11px] font-800 uppercase tracking-widest text-cream/50">Stripe configuration</p>
          <span className={`text-xs font-800 px-2 py-1 rounded-full border-2 ${cfg.mode === "live" ? "bg-grass border-grass text-white" : "border-sunny text-sunny"}`}>
            {cfg.mode === "live" ? "LIVE" : "TEST mode"}
          </span>
        </div>
        <p className="text-xs text-cream/50 mb-4">Add your live secret key + recurring Price IDs to enable subscriptions, refunds and cancellations. Stored encrypted.</p>
        <div className="space-y-3">
          <input type="password" value={form.stripe_secret_key} onChange={(e) => setForm({ ...form, stripe_secret_key: e.target.value })}
            data-testid="stripe-key-input" placeholder={cfg.has_key ? "•••••••• (key saved — leave blank to keep)" : "sk_live_…"}
            className="w-full bg-ink border-2 border-cream/30 rounded-xl px-3 py-2.5 text-sm text-cream focus:border-orange outline-none" />
          <div className="grid sm:grid-cols-2 gap-3">
            <input value={form.team_price_id} onChange={(e) => setForm({ ...form, team_price_id: e.target.value })}
              data-testid="team-price-input" placeholder="Team price_id (price_…)"
              className="bg-ink border-2 border-cream/30 rounded-xl px-3 py-2.5 text-sm text-cream focus:border-orange outline-none" />
            <input value={form.business_price_id} onChange={(e) => setForm({ ...form, business_price_id: e.target.value })}
              data-testid="business-price-input" placeholder="Business price_id (price_…)"
              className="bg-ink border-2 border-cream/30 rounded-xl px-3 py-2.5 text-sm text-cream focus:border-orange outline-none" />
          </div>
          <button onClick={save} disabled={saving} data-testid="save-billing-config"
            className="px-4 py-2.5 rounded-xl text-sm font-800 bg-orange text-white border-2 border-orange disabled:opacity-60">
            {saving ? "Saving…" : "Save settings"}
          </button>
        </div>
      </div>

      <div className="rounded-2xl border-2 border-cream/20 overflow-hidden" data-testid="transactions-table">
        <div className="px-5 py-3 bg-cream/[0.06] text-[11px] uppercase tracking-widest font-800 text-cream/60">Transactions</div>
        {data.transactions.length === 0 ? (
          <p className="text-cream/50 text-sm p-8 text-center">No transactions yet.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="text-cream/50 text-[11px] uppercase tracking-widest font-800">
                <Th>Customer</Th><Th>Plan</Th><Th className="text-right">Amount</Th><Th>Status</Th><Th>Date</Th><Th></Th>
              </tr></thead>
              <tbody className="divide-y divide-cream/10">
                {data.transactions.map((t) => (
                  <tr key={t.session_id} className="hover:bg-cream/[0.03]" data-testid={`txn-${t.session_id}`}>
                    <Td className="text-cream/80">{t.email || "—"}</Td>
                    <Td className="capitalize">{t.plan_id}</Td>
                    <Td className="text-right font-mono">${t.amount}</Td>
                    <Td>
                      <span className={`text-xs font-800 px-2 py-0.5 rounded-full border ${t.payment_status === "paid" ? "bg-grass border-grass text-white" : t.payment_status === "refunded" ? "bg-sunny/20 border-sunny text-sunny" : "border-cream/30 text-cream/60"}`}>
                        {t.payment_status}
                      </span>
                    </Td>
                    <Td className="text-cream/60 font-mono text-xs whitespace-nowrap">{fmtDate(t.created_at)}</Td>
                    <Td className="text-right">
                      {t.payment_status === "paid" && (
                        <button onClick={() => refund(t.session_id)} data-testid={`refund-${t.session_id}`}
                          className="text-xs font-800 px-2.5 py-1 rounded-lg border-2 border-cream/30 hover:border-danger hover:bg-danger hover:text-white">Refund</button>
                      )}
                    </Td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function PromoAdmin() {
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    apiClient.get("/platform/promo").then(({ data }) => setCfg(data)).catch(() => setCfg({}));
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await apiClient.put("/platform/promo", cfg);
      toast.success("Growth settings saved");
    } catch { toast.error("Failed to save"); } finally { setSaving(false); }
  };

  if (!cfg) return <Loading />;
  const Toggle = ({ on, onClick, testid }) => (
    <button type="button" onClick={onClick} data-testid={testid} aria-pressed={on}
      className={`relative h-7 w-12 rounded-full border-2 border-cream/40 transition-colors shrink-0 ${on ? "bg-grass" : "bg-ink"}`}>
      <span className={`absolute top-[2px] h-5 w-5 rounded-full border-2 border-cream/40 bg-cream transition-all ${on ? "left-[22px]" : "left-[2px]"}`} />
    </button>
  );

  return (
    <div className="space-y-6" data-testid="promo-admin">
      <div className="rounded-2xl border-2 border-cream/20 bg-cream/[0.04] p-6 space-y-5">
        <div>
          <p className="text-[11px] font-800 uppercase tracking-widest text-cream/50 mb-1">Free trial</p>
          <p className="text-xs text-cream/50">New accounts get the full Business experience free for this many days, then fall back to their plan.</p>
        </div>
        <div className="flex items-center justify-between">
          <span className="text-sm font-700">Trial enabled for new signups</span>
          <Toggle on={!!cfg.trial_enabled} onClick={() => setCfg({ ...cfg, trial_enabled: !cfg.trial_enabled })} testid="promo-trial-toggle" />
        </div>
        <div className="flex items-center justify-between gap-4">
          <span className="text-sm font-700">Trial length (days)</span>
          <input type="number" min="0" max="365" value={cfg.trial_days} onChange={(e) => setCfg({ ...cfg, trial_days: Number(e.target.value) })}
            data-testid="promo-trial-days" className="w-24 bg-ink border-2 border-cream/30 rounded-xl px-3 py-2 text-sm text-cream focus:border-orange outline-none text-right" />
        </div>
      </div>

      <div className="rounded-2xl border-2 border-cream/20 bg-cream/[0.04] p-6 space-y-5">
        <div>
          <p className="text-[11px] font-800 uppercase tracking-widest text-cream/50 mb-1">Marketing banner</p>
          <p className="text-xs text-cream/50">Shown at the top of the public landing page to promote the offer.</p>
        </div>
        <div className="flex items-center justify-between">
          <span className="text-sm font-700">Show banner on landing page</span>
          <Toggle on={!!cfg.banner_enabled} onClick={() => setCfg({ ...cfg, banner_enabled: !cfg.banner_enabled })} testid="promo-banner-toggle" />
        </div>
        <div>
          <label className="text-sm font-700 block mb-2">Banner text</label>
          <textarea value={cfg.banner_text} onChange={(e) => setCfg({ ...cfg, banner_text: e.target.value.slice(0, 200) })}
            data-testid="promo-banner-text-input" rows={2} maxLength={200}
            className="w-full bg-ink border-2 border-cream/30 rounded-xl px-3 py-2.5 text-sm text-cream focus:border-orange outline-none" />
        </div>
        <div className="flex flex-wrap gap-6">
          <div>
            <label className="text-sm font-700 block mb-2">Background color</label>
            <div className="flex items-center gap-2">
              <input type="color" value={cfg.banner_bg || "#1A1A1A"} onChange={(e) => setCfg({ ...cfg, banner_bg: e.target.value })}
                data-testid="promo-banner-bg" className="color-swatch h-10 w-12 border-2 border-cream/30 rounded-lg cursor-pointer" />
              <input value={cfg.banner_bg || "#1A1A1A"} onChange={(e) => setCfg({ ...cfg, banner_bg: e.target.value })}
                className="w-24 bg-ink border-2 border-cream/30 rounded-lg px-2 py-1.5 text-xs font-mono text-cream outline-none" />
            </div>
          </div>
          <div>
            <label className="text-sm font-700 block mb-2">Text color</label>
            <div className="flex items-center gap-2">
              <input type="color" value={cfg.banner_fg || "#FFF7ED"} onChange={(e) => setCfg({ ...cfg, banner_fg: e.target.value })}
                data-testid="promo-banner-fg" className="color-swatch h-10 w-12 border-2 border-cream/30 rounded-lg cursor-pointer" />
              <input value={cfg.banner_fg || "#FFF7ED"} onChange={(e) => setCfg({ ...cfg, banner_fg: e.target.value })}
                className="w-24 bg-ink border-2 border-cream/30 rounded-lg px-2 py-1.5 text-xs font-mono text-cream outline-none" />
            </div>
          </div>
        </div>
        <div className="flex flex-wrap gap-4">
          <div className="flex-1 min-w-[180px]">
            <label className="text-sm font-700 block mb-2">Button text</label>
            <input value={cfg.banner_cta_text ?? "Start free \u2192"} onChange={(e) => setCfg({ ...cfg, banner_cta_text: e.target.value.slice(0, 40) })}
              data-testid="promo-banner-cta-text" maxLength={40} placeholder="Start free →"
              className="w-full bg-ink border-2 border-cream/30 rounded-xl px-3 py-2.5 text-sm text-cream focus:border-orange outline-none" />
            <p className="text-[11px] text-cream/40 mt-1">Leave empty to hide the link entirely.</p>
          </div>
          <div className="flex-1 min-w-[180px]">
            <label className="text-sm font-700 block mb-2">Button link</label>
            <input value={cfg.banner_cta_url ?? "/register"} onChange={(e) => setCfg({ ...cfg, banner_cta_url: e.target.value.slice(0, 300) })}
              data-testid="promo-banner-cta-url" maxLength={300} placeholder="/register or https://…"
              className="w-full bg-ink border-2 border-cream/30 rounded-xl px-3 py-2.5 text-sm text-cream font-mono focus:border-orange outline-none" />
            <p className="text-[11px] text-cream/40 mt-1">Internal path (e.g. <code>/register</code>) or full URL (opens in new tab).</p>
          </div>
        </div>
        {cfg.banner_enabled && (
          <div className="rounded-xl border-2 border-cream/15 px-4 py-2.5 text-center text-xs sm:text-sm font-700" data-testid="promo-banner-preview"
            style={{ backgroundColor: cfg.banner_bg || "#1A1A1A", color: cfg.banner_fg || "#FFF7ED" }}>
            {cfg.banner_text} {(cfg.banner_cta_text ?? "Start free \u2192") && <span className="underline decoration-orange decoration-2 underline-offset-2 text-orange">{cfg.banner_cta_text ?? "Start free \u2192"}</span>}
          </div>
        )}
      </div>

      <div className="bg-ink/60 border-2 border-cream/15 rounded-2xl p-5 space-y-4" data-testid="lifetime-config-card">
        <div className="flex items-center justify-between gap-3">
          <div>
            <h3 className="font-800 text-cream flex items-center gap-2"><Sparkles className="h-4 w-4 text-orange" /> Lifetime founding offer</h3>
            <p className="text-[11px] text-cream/50 mt-0.5">Shows a floating "spots left" widget on the homepage. The first N sign-ups are auto-granted a permanent Business plan.</p>
          </div>
          <button onClick={() => setCfg({ ...cfg, lifetime_enabled: !cfg.lifetime_enabled })} data-testid="lifetime-enabled-toggle" aria-pressed={!!cfg.lifetime_enabled}
            className={`relative h-7 w-12 rounded-full border-2 border-cream/40 transition-colors shrink-0 ${cfg.lifetime_enabled ? "bg-grass" : "bg-cream/10"}`}>
            <span className={`absolute top-[2px] h-5 w-5 rounded-full border-2 border-cream/40 bg-white transition-all ${cfg.lifetime_enabled ? "left-[22px]" : "left-[2px]"}`} />
          </button>
        </div>
        <div className="flex flex-wrap gap-4">
          <div className="w-32">
            <label className="text-sm font-700 block mb-2">Total spots</label>
            <input type="number" min={0} value={cfg.lifetime_spots ?? 30} onChange={(e) => setCfg({ ...cfg, lifetime_spots: Math.max(0, parseInt(e.target.value || "0", 10)) })}
              data-testid="lifetime-spots-input" className="w-full bg-ink border-2 border-cream/30 rounded-xl px-3 py-2.5 text-sm text-cream focus:border-orange outline-none" />
          </div>
          <div className="flex-1 min-w-[200px]">
            <label className="text-sm font-700 block mb-2">Headline</label>
            <input value={cfg.lifetime_headline ?? "Founding lifetime membership"} onChange={(e) => setCfg({ ...cfg, lifetime_headline: e.target.value.slice(0, 80) })}
              data-testid="lifetime-headline-input" maxLength={80} placeholder="Founding lifetime membership"
              className="w-full bg-ink border-2 border-cream/30 rounded-xl px-3 py-2.5 text-sm text-cream focus:border-orange outline-none" />
          </div>
        </div>
        <p className="text-[11px] text-cream/40">Counting starts the moment you enable this — only sign-ups from now on claim spots (existing accounts are unaffected).</p>
      </div>

      <button onClick={save} disabled={saving} data-testid="save-promo-config"
        className="px-4 py-2.5 rounded-xl text-sm font-800 bg-orange text-white border-2 border-orange disabled:opacity-60">
        {saving ? "Saving…" : "Save growth settings"}
      </button>
    </div>
  );
}


function Leads({ leads }) {
  if (!leads) return <Loading />;
  if (leads.length === 0) return <Empty label="No enterprise leads yet. They'll appear here when someone submits the 'Talk to sales' form." />;
  return (
    <div className="rounded-2xl border-2 border-cream/20 overflow-hidden" data-testid="leads-table">
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-cream/[0.06] text-cream/60 text-[11px] uppercase tracking-widest font-800">
              <Th>Name</Th><Th>Email</Th><Th>Company</Th><Th>Message</Th><Th>Received</Th>
            </tr>
          </thead>
          <tbody className="divide-y divide-cream/10">
            {leads.map((l) => (
              <tr key={l.id} className="hover:bg-cream/[0.03] align-top" data-testid={`lead-row-${l.id}`}>
                <Td className="font-700">{l.name}</Td>
                <Td className="text-cream/70">{l.email}</Td>
                <Td className="text-cream/60">{l.company || "—"}</Td>
                <Td className="text-cream/70 max-w-sm">{l.message || "—"}</Td>
                <Td className="text-cream/60 font-mono text-xs whitespace-nowrap">{fmtDate(l.created_at)}</Td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Support({ tickets, reload }) {
  const [active, setActive] = useState(null);
  const [reply, setReply] = useState("");
  const [sending, setSending] = useState(false);

  if (!tickets) return <Loading />;
  if (tickets.length === 0) return <Empty label="No support tickets yet. In-app and website contact requests will appear here." />;

  const CAT = {
    billing: "bg-orange text-white", technical: "bg-teal text-ink",
    account: "bg-sunny text-ink", general: "bg-cream/20 text-cream",
  };

  const setStatus = async (t, status) => {
    try { await apiClient.post(`/platform/support/${t.id}/status`, { status }); toast.success(`Marked ${status}`); reload(); if (active?.id === t.id) setActive({ ...active, status }); }
    catch { toast.error("Failed to update"); }
  };
  const sendReply = async () => {
    if (!reply.trim()) return;
    setSending(true);
    try {
      const { data } = await apiClient.post(`/platform/support/${active.id}/reply`, { message: reply });
      toast.success(data.email_sent ? "Reply sent by email" : "Reply saved (email not configured)");
      const newReply = { by: "You", message: reply, at: new Date().toISOString() };
      setActive({ ...active, replies: [...(active.replies || []), newReply], status: "open" });
      setReply(""); reload();
    } catch { toast.error("Failed to send reply"); }
    finally { setSending(false); }
  };

  return (
    <div className="grid lg:grid-cols-5 gap-5" data-testid="support-panel">
      <div className="lg:col-span-2 rounded-2xl border-2 border-cream/20 overflow-hidden">
        <div className="divide-y divide-cream/10 max-h-[70vh] overflow-y-auto">
          {tickets.map((t) => (
            <button key={t.id} onClick={() => { setActive(t); setReply(""); }} data-testid={`ticket-row-${t.id}`}
              className={`w-full text-left px-4 py-3.5 hover:bg-cream/[0.05] transition-colors ${active?.id === t.id ? "bg-cream/[0.08]" : ""}`}>
              <div className="flex items-center gap-2 mb-1">
                <span className={`text-[10px] font-800 uppercase tracking-wide px-2 py-0.5 rounded-full ${CAT[t.category] || CAT.general}`}>{t.category}</span>
                {t.source === "web" && <span className="text-[10px] font-700 text-cream/40">web</span>}
                {t.status === "resolved"
                  ? <span className="ml-auto text-[10px] font-700 text-grass flex items-center gap-1"><CheckCircle2 className="h-3 w-3" /> resolved</span>
                  : <span className="ml-auto text-[10px] font-700 text-sunny">open</span>}
              </div>
              <p className="font-700 text-sm truncate">{t.subject}</p>
              <p className="text-xs text-cream/50 truncate">{t.email}{t.org_name ? ` · ${t.org_name}` : ""}</p>
            </button>
          ))}
        </div>
      </div>

      <div className="lg:col-span-3 rounded-2xl border-2 border-cream/20 p-5">
        {!active ? (
          <div className="flex items-center justify-center h-full py-16 text-cream/40 font-600">Select a ticket to view and reply.</div>
        ) : (
          <div data-testid="ticket-detail">
            <div className="flex items-start justify-between gap-3 mb-3">
              <div>
                <h3 className="font-display text-xl font-700">{active.subject}</h3>
                <p className="text-sm text-cream/60">{active.name || "—"} · {active.email}{active.org_name ? ` · ${active.org_name}` : ""}</p>
                <p className="text-xs text-cream/40 font-mono mt-0.5">{fmtDate(active.created_at)}</p>
              </div>
              {active.status === "resolved"
                ? <button onClick={() => setStatus(active, "open")} data-testid="ticket-reopen" className="shrink-0 text-xs font-800 px-3 py-1.5 rounded-xl border-2 border-cream/30 hover:border-cream/60">Reopen</button>
                : <button onClick={() => setStatus(active, "resolved")} data-testid="ticket-resolve" className="shrink-0 text-xs font-800 px-3 py-1.5 rounded-xl border-2 border-grass text-grass hover:bg-grass hover:text-ink">Mark resolved</button>}
            </div>
            <p className="text-sm bg-cream/[0.06] border-2 border-cream/15 rounded-2xl p-4 whitespace-pre-wrap mb-4">{active.message}</p>

            {(active.replies || []).length > 0 && (
              <div className="space-y-2 mb-4">
                {active.replies.map((r, i) => (
                  <div key={i} className="text-sm bg-orange/15 border-2 border-orange/30 rounded-2xl p-3" data-testid={`ticket-reply-${i}`}>
                    <p className="text-[11px] font-800 text-orange uppercase tracking-wide mb-1">{r.by} · {fmtDate(r.at)}</p>
                    <p className="whitespace-pre-wrap">{r.message}</p>
                  </div>
                ))}
              </div>
            )}

            <textarea value={reply} onChange={(e) => setReply(e.target.value)} rows={3} data-testid="ticket-reply-input"
              placeholder="Write a reply… (emails the customer via Resend)"
              className="w-full bg-cream/[0.06] border-2 border-cream/20 rounded-2xl p-3 text-sm text-cream placeholder:text-cream/30 focus:border-orange outline-none" />
            <button onClick={sendReply} disabled={sending || !reply.trim()} data-testid="ticket-send-reply"
              className="mt-2 flex items-center gap-2 text-sm font-800 px-4 py-2.5 rounded-xl bg-orange text-white border-2 border-orange disabled:opacity-50 hover:bg-orange/90">
              {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Send reply
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function Empty({ label }) {
  return (
    <div className="rounded-2xl border-2 border-cream/20 bg-cream/[0.04] p-16 text-center">
      <p className="text-cream/50 font-600">{label}</p>
    </div>
  );
}

const Th = ({ children, className = "" }) => <th className={`text-left px-4 py-3 ${className}`}>{children}</th>;
const Td = ({ children, className = "" }) => <td className={`px-4 py-3 ${className}`}>{children}</td>;
